import {
  AgencyProfileCapability,
  NavigationItem,
  Role,
  type AgencyProfileFieldsFragment,
  type AgencyProfileInput,
  type AgencyUserHomePinInput,
  type GetAgencyProfileCapabilityCatalogQuery
} from '~/api/graphql';

export type CapabilityCatalog =
  GetAgencyProfileCapabilityCatalogQuery['agencyProfileCapabilityCatalog'];
export type CapabilityGroup = CapabilityCatalog['groups'][number];
export type CapabilitySection = CapabilityGroup['sections'][number];
export type CapabilityOption = CapabilitySection['capabilities'][number];
export type NavigationOption = CapabilityOption['navigation'][number];

/** Every section in the catalog, flattened out of its group. */
const allSections = (catalog: CapabilityCatalog): CapabilitySection[] =>
  catalog.groups.flatMap((group) => group.sections);

/** Every capability option in the catalog, in display order. */
const allOptions = (catalog: CapabilityCatalog): CapabilityOption[] =>
  allSections(catalog).flatMap((section) => section.capabilities);

const DESIGN_WORDING: Partial<Record<AgencyProfileCapability, { label: string; hint?: string }>> = {
  [AgencyProfileCapability.LibraryStylesUse]: {
    label: 'Use designs',
    hint: "Apply the agency's designs to generated documents, and open Library › Styles."
  },
  [AgencyProfileCapability.LibraryTemplatesManage]: {
    label: 'Create and edit designs and templates'
  }
};

/**
 * The catalog as it reads once designs take over from Styles. Designs are granted by the Styles
 * capabilities, so only their wording changes, never what a profile stores.
 */
export const withDesignWording = (catalog: CapabilityCatalog): CapabilityCatalog => ({
  ...catalog,
  groups: catalog.groups.map((group) => ({
    ...group,
    sections: group.sections.map((section) => ({
      ...section,
      title: section.capabilities.some(
        (option) => option.capability === AgencyProfileCapability.LibraryStylesUse
      )
        ? 'Designs and templates'
        : section.title,
      capabilities: section.capabilities.map((option) => ({
        ...option,
        ...DESIGN_WORDING[option.capability]
      }))
    }))
  }))
});

/**
 * The detail page's working copy.
 *
 * Capabilities, not permissions, are what the page edits: a permission like
 * FILE:UPLOAD belongs to no single product decision, so the server derives
 * those from what an admin ticks here.
 */
export type AgencyProfileFormInput = {
  name: string;
  description: string;
  isDefault: boolean;
  askCaraEnabled: boolean;
  allowUserCustomization: boolean;
  pins: AgencyUserHomePinInput[];
  navigation: NavigationItem[];
  capabilities: AgencyProfileCapability[];
  teamIds: string[];
  userIds: string[];
};

export const agencyProfileToFormInput = (
  profile: AgencyProfileFieldsFragment
): AgencyProfileFormInput => ({
  name: profile.name,
  description: profile.description,
  isDefault: profile.isDefault,
  askCaraEnabled: profile.configuration.homeScreen.askCaraEnabled,
  allowUserCustomization: profile.configuration.homeScreen.allowUserCustomization,
  pins: profile.configuration.homeScreen.pins.map((pin) => ({
    useCase: pin.useCase,
    customPresetId: pin.customPresetId
  })),
  navigation: [...profile.configuration.navigation],
  capabilities: [...profile.configuration.capabilities],
  teamIds: profile.teams.map((team) => team.id),
  userIds: profile.users.map((user) => user.id)
});

export const formInputToAgencyProfileInput = (
  form: AgencyProfileFormInput
): AgencyProfileInput => ({
  name: form.name,
  description: form.description,
  isDefault: form.isDefault,
  homeScreen: {
    askCaraEnabled: form.askCaraEnabled,
    allowUserCustomization: form.allowUserCustomization,
    pins: form.pins
  },
  navigation: form.navigation,
  capabilities: form.capabilities,
  teamIds: form.teamIds,
  userIds: form.userIds
});

/**
 * The users an admin may assign to a profile. Agency admins are excluded:
 * profiles never apply to them, and the server rejects an admin in `userIds`,
 * so listing one would offer a choice that cannot be saved.
 */
export const assignableAgencyUsers = <T extends { roles: Role[] }>(users: T[]): T[] =>
  users.filter((user) => !user.roles.includes(Role.Admin));

/** The fields needed to resolve which profile governs a user. */
export type AgencyProfileAssignment = {
  id: string;
  name: string;
  isDefault: boolean;
  users: { id: string }[];
  teams: { id: string }[];
};

/**
 * Which profile governs a user, in the same order the server uses: a direct
 * assignment wins, then the oldest profile among their teams, then the default.
 */
export const profileGoverningUser = (
  userId: string,
  profiles: AgencyProfileAssignment[],
  teamIdsForUser: Set<string>
): AgencyProfileAssignment | null => {
  for (const profile of profiles) {
    if (profile.users.some((user) => user.id === userId)) {
      return profile;
    }
  }

  for (const profile of profiles) {
    if (profile.teams.some((team) => teamIdsForUser.has(team.id))) {
      return profile;
    }
  }

  return profiles.find((profile) => profile.isDefault) ?? null;
};

/** Whether a user reaches a profile by direct assignment or a team on it, not the default fallback. */
export const userExplicitlyOnProfile = (
  userId: string,
  profile: AgencyProfileAssignment,
  teamIdsForUser: Set<string>
): boolean => {
  if (profile.users.some((user) => user.id === userId)) {
    return true;
  }
  return profile.teams.some((team) => teamIdsForUser.has(team.id));
};

/** Toggles one value in a list, preserving the order of everything else. */
export const toggleInList = <T>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

export const grantsCapability = (
  form: AgencyProfileFormInput,
  capability: AgencyProfileCapability
): boolean => form.capabilities.includes(capability);

/** Every capability in the catalog, in display order. */
export const allCapabilities = (catalog: CapabilityCatalog): AgencyProfileCapability[] =>
  allOptions(catalog).map((option) => option.capability);

/** Capabilities that name `capability` among their prerequisites. */
export const dependentsOf = (
  catalog: CapabilityCatalog,
  capability: AgencyProfileCapability
): AgencyProfileCapability[] =>
  allOptions(catalog)
    .filter((option) => option.requires.includes(capability))
    .map((option) => option.capability);

/**
 * Sidebar entries the granted capabilities make reachable, in catalog order.
 * The navigation editor offers exactly these: a page no capability opens would
 * be a sidebar row leading to a permission error.
 */
export const reachableNavigation = (
  catalog: CapabilityCatalog,
  capabilities: AgencyProfileCapability[]
): NavigationOption[] => {
  const seen = new Set<NavigationItem>();
  const options: NavigationOption[] = [];
  for (const option of allOptions(catalog)) {
    if (!capabilities.includes(option.capability)) {
      continue;
    }
    for (const navigation of option.navigation) {
      if (!seen.has(navigation.item)) {
        seen.add(navigation.item);
        options.push(navigation);
      }
    }
  }
  return options;
};

/**
 * Ticks or unticks a capability along with the chain it belongs to.
 *
 * Ticking brings in everything it requires, because a capability without its
 * prerequisites opens a surface its user cannot load. Unticking removes
 * whatever required it, for the same reason from the other end — an admin who
 * takes away "run presets" has taken away editing them too, and the checkbox
 * should say so rather than leaving a grant that no longer means anything.
 *
 * Navigation follows: entries no surviving capability reaches are dropped, and
 * entries a newly granted capability opens are shown. An admin can hide a page
 * afterwards; they cannot show one nothing opens.
 */
export const applyCapabilityToggle = (
  form: AgencyProfileFormInput,
  catalog: CapabilityCatalog,
  option: CapabilityOption
): AgencyProfileFormInput => {
  const turningOn = !form.capabilities.includes(option.capability);

  let capabilities: AgencyProfileCapability[];
  if (turningOn) {
    const added = [...option.requires, option.capability].filter(
      (capability) => !form.capabilities.includes(capability)
    );
    capabilities = [...form.capabilities, ...added];
  } else {
    const removed = new Set<AgencyProfileCapability>([option.capability]);
    // Walk dependents until the set stops growing: a chain three deep has to
    // come out whole, not one link per click.
    for (;;) {
      const next = [...removed].flatMap((capability) => dependentsOf(catalog, capability));
      const before = removed.size;
      next.forEach((capability) => removed.add(capability));
      if (removed.size === before) {
        break;
      }
    }
    capabilities = form.capabilities.filter((capability) => !removed.has(capability));
  }

  const ordered = allCapabilities(catalog).filter((capability) =>
    capabilities.includes(capability)
  );

  // Only the entries this toggle changed move. Showing everything newly
  // reachable would resurrect a page the admin hid in some other section, and
  // dropping everything unreachable is exactly what revoking should do.
  const before = reachableNavigation(catalog, form.capabilities).map((option) => option.item);
  const after = reachableNavigation(catalog, ordered).map((option) => option.item);
  const navigation = turningOn
    ? [
        ...form.navigation,
        ...after.filter((item) => !before.includes(item) && !form.navigation.includes(item))
      ]
    : form.navigation.filter((item) => after.includes(item));

  return { ...form, capabilities: ordered, navigation };
};

/**
 * Shows or hides one sidebar entry, within what the granted capabilities allow.
 */
export const applyNavigationToggle = (
  form: AgencyProfileFormInput,
  option: NavigationOption
): AgencyProfileFormInput => ({
  ...form,
  navigation: toggleInList(form.navigation, option.item)
});

/**
 * Summarizes a section for its collapsed row: none, all, or how far in between.
 */
export const capabilitySummary = (
  form: AgencyProfileFormInput,
  section: CapabilitySection
): { label: string; tone: 'none' | 'partial' | 'full' } => {
  const granted = section.capabilities.filter((option) =>
    form.capabilities.includes(option.capability)
  ).length;
  if (granted === 0) {
    return { label: 'No access', tone: 'none' };
  }
  if (granted >= section.capabilities.length) {
    return { label: 'Full access', tone: 'full' };
  }
  return { label: `${granted} of ${section.capabilities.length}`, tone: 'partial' };
};
